/* eslint-disable no-param-reassign */
/* eslint-disable no-console */

/**
 * Reusable image upload input.
 * - Default mode: center crop to 16:9, resize to 320x180, remove alpha.
 * - isProjectImg=true mode: PNG with exact 256x256 dimensions (BasicInfo behavior).
 */

import React, { useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import PersonIcon from "@mui/icons-material/Person";

const WIDTH = 320;
const HEIGHT = 180;
const BACKGROUND_COLOR = "#FFFFFF";
const MAX_BYTES = 4 * 1024 * 1024; // 4 MB (entrada)
const MAX_OUTPUT_BYTES = 1024 * 1024; // 1 MB (salida)

function UploadImageInput({
  label,
  helpText,
  inputId,
  selectLabel,
  deleteLabel,
  uploadPlaceholder,
  projectId,
  mainUserUid,
  existingImageUid,
  formImageUid,
  onChangeImageUid,
  onNewAlertMessage,
  onSetImageData,
  onDeleteImage,
  isProjectImg,
}) {
  const [imagePreview, setImagePreview] = useState(null);
  const [hideExistingImage, setHideExistingImage] = useState(false);
  const fileInputRef = useRef(null);

  const existingImageUrl = useMemo(() => {
    const uidToUse = formImageUid || existingImageUid;

    if (!mainUserUid || !projectId || !uidToUse) return null;

    return `https://onnaliving.com/userimagefiles/${mainUserUid}/${projectId}/${uidToUse}.png`;
  }, [mainUserUid, projectId, existingImageUid, formImageUid]);

  const currentImageUrl = imagePreview || (hideExistingImage ? null : existingImageUrl) || null;

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const dataUrlToImage = (dataUrl) =>
    new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl;
    });

  const normalizePngTo16x9 = async (file) => {
    const dataUrl = await fileToDataUrl(file);
    const img = await dataUrlToImage(dataUrl);

    const srcW = img.naturalWidth || img.width;
    const srcH = img.naturalHeight || img.height;

    const targetRatio = WIDTH / HEIGHT;
    const srcRatio = srcW / srcH;

    let cropW = srcW;
    let cropH = srcH;
    let cropX = 0;
    let cropY = 0;

    if (srcRatio > targetRatio) {
      cropW = Math.round(srcH * targetRatio);
      cropX = Math.round((srcW - cropW) / 2);
    } else if (srcRatio < targetRatio) {
      cropH = Math.round(srcW / targetRatio);
      cropY = Math.round((srcH - cropH) / 2);
    }

    const canvas = document.createElement("canvas");

    canvas.width = WIDTH;
    canvas.height = HEIGHT;

    const ctx = canvas.getContext("2d", { alpha: false });

    ctx.fillStyle = BACKGROUND_COLOR;

    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, WIDTH, HEIGHT);

    return canvas.toDataURL("image/png");
  };

  const bytesFromDataUrl = (dataUrl) => {
    const base64 = String(dataUrl).split(",")[1] || "";

    const padding = base64.match(/=*$/)?.[0]?.length || 0;

    return Math.floor((base64.length * 3) / 4) - padding;
  };

  const handleFileChange = async (ev) => {
    const file = ev.target.files?.[0];

    if (!file) return;

    ev.target.value = "";

    if (file.type !== "image/png") {
      onNewAlertMessage({ type: "warning", title: "image", content: "invalidFormat" });
      return;
    }

    if (isProjectImg) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const img = await dataUrlToImage(dataUrl);
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;

        if (width !== 256 || height !== 256) {
          onNewAlertMessage({ type: "warning", title: "image", content: "wrongSize" });
          return;
        }

        setImagePreview(dataUrl);
        setHideExistingImage(false);
        onSetImageData(dataUrl);
        onDeleteImage(false);
        onChangeImageUid(formImageUid || existingImageUid || null);
      } catch (e) {
        console.log(e);
        onNewAlertMessage({ type: "error", title: "image", content: "unknownError" });
      }
      return;
    }

    if (file.size > MAX_BYTES) {
      onNewAlertMessage({ type: "warning", title: "image", content: "fileTooLarge" });
      return;
    }

    try {
      const normalizedPngDataUrl = await normalizePngTo16x9(file);

      if (!normalizedPngDataUrl.startsWith("data:image/png;base64,")) {
        onNewAlertMessage({ type: "warning", title: "image", content: "invalidFormat" });
        return;
      }

      const finalBytes = bytesFromDataUrl(normalizedPngDataUrl);

      if (finalBytes > MAX_OUTPUT_BYTES) {
        onNewAlertMessage({ type: "warning", title: "image", content: "fileTooLarge" });
        return;
      }

      setImagePreview(normalizedPngDataUrl);
      setHideExistingImage(false);
      onSetImageData(normalizedPngDataUrl);
      onDeleteImage(false);
      onChangeImageUid(formImageUid || existingImageUid || null);
    } catch (e) {
      console.log(e);
      onNewAlertMessage({ type: "error", title: "image", content: "unknownError" });
    }
  };

  const deleteSelectedImage = () => {
    setImagePreview(null);
    setHideExistingImage(true);
    onSetImageData(null);
    onDeleteImage(true);
    onChangeImageUid(null);
  };

  const fileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <MDTypography variant="h6">{label}</MDTypography>
      <MDTypography variant="button" fontWeight="regular" color="text">
        {helpText}
      </MDTypography>

      <MDBox mt={1} display="flex" flexDirection="column" alignItems="center">
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept="image/png"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {isProjectImg ? (
          <MDBox
            width="255px"
            height="255px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ cursor: "pointer" }}
            onClick={fileSelect}
            title={selectLabel}
          >
            <MDAvatar
              alt={currentImageUrl ? "project-image" : ""}
              src={currentImageUrl || ""}
              size="xxl"
              shadow="sm"
              bgColor="secondary"
            >
              {!currentImageUrl && <PersonIcon sx={{ fontSize: 42 }} />}
            </MDAvatar>
          </MDBox>
        ) : (
          <MDBox
            width="320px"
            height="180px"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              border: "1px solid rgba(0,0,0,0.12)",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={fileSelect}
            title={selectLabel}
          >
            {currentImageUrl ? (
              <img
                alt="uploaded"
                src={currentImageUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <MDBox>{uploadPlaceholder}</MDBox>
            )}
          </MDBox>
        )}

        <MDBox mt={2}>
          <MDButton size="small" variant="contained" onClick={fileSelect}>
            {selectLabel}
          </MDButton>
          {Boolean(deleteLabel) && Boolean(currentImageUrl) && (
            <MDButton size="small" variant="contained" onClick={deleteSelectedImage} sx={{ ml: 1 }}>
              {deleteLabel}
            </MDButton>
          )}
        </MDBox>
      </MDBox>
    </>
  );
}

UploadImageInput.propTypes = {
  label: PropTypes.string.isRequired,
  helpText: PropTypes.string.isRequired,
  inputId: PropTypes.string,
  selectLabel: PropTypes.string.isRequired,
  deleteLabel: PropTypes.string,
  uploadPlaceholder: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  mainUserUid: PropTypes.string,
  existingImageUid: PropTypes.string,
  formImageUid: PropTypes.string,
  onChangeImageUid: PropTypes.func.isRequired,
  onNewAlertMessage: PropTypes.func.isRequired,
  onSetImageData: PropTypes.func.isRequired,
  onDeleteImage: PropTypes.func,
  isProjectImg: PropTypes.bool,
};

UploadImageInput.defaultProps = {
  inputId: "",
  deleteLabel: "",
  projectId: "",
  mainUserUid: "",
  existingImageUid: "",
  formImageUid: "",
  onDeleteImage: () => {},
  isProjectImg: false,
};

export default UploadImageInput;
